import React, { ReactElement, useState, ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PopUp,
  EditCard,
  StyledIcon,
  Cards,
  StyledAnswers,
} from '../../../../styled-components'

import { Part, Question } from '../../../../../../types/training'
import Icon from '../../../../../icon'
import { Alert } from '../../../../../../constants/sweet-alert'
import Button from '../../../../../button'
import Text from '../../../../../text'
import { addQuestion, updateQuestion } from '../../../../../../api/question'
import InputArea from '../../../../../input/input-area'
import Answer from './preview-answers'
import QuizTick from './quiz-tick'
import QuizSelect from './quiz-select'
import QuizTrueFalse from './quiz-true-false'

interface PartEditProps {
  question: Question
  onClose: () => void
  part: Part
}

const PartAddEditQuiz = ({
  question,
  onClose,
  part,
}: PartEditProps): ReactElement => {
  const [text, setText] = useState(question.text)
  const [answers, setAnswers] = useState(question.answers)
  const [correctAnswers, setCorrectAnswers] = useState(question.correctAnswers)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const onSaveEdit = () => {
    setLoading(true)

    const payload = {
      text,
      answers,
      correctAnswers,
    }
    updateQuestion(question.id, payload).then((status) => {
      if (status === 200) {
        setLoading(false)
        Alert.fire({
          icon: 'success',
          title: `Updated`,
        })
        navigate(0)
      } else {
        setLoading(false)
        Alert.fire({
          icon: 'error',
          title: `Error`,
        })
      }
    })
  }

  const onSaveAdd = () => {
    setLoading(true)
    const partId = part.id
    const payload = {
      question: {
        text,
        answers,
        correctAnswers,
      },
      partId,
    }
    addQuestion(payload).then((status) => {
      if (status === 201) {
        setLoading(false)
        Alert.fire({
          icon: 'success',
          title: `Updated`,
        })
        navigate(0)
      } else {
        setLoading(false)
        Alert.fire({
          icon: 'error',
          title: `Error`,
        })
      }
    })
  }

  const onAdd = () => {
    const oldAnswers = [...answers]
    oldAnswers.push(' ')
    setAnswers(oldAnswers)
  }

  const onEdit = (answer: string | null, index: number) => {
    if (answer) {
      const editAnswer = answer.trim()
      const oldAnswers = [...answers]
      const oldCorrectAnswersEdit = [...correctAnswers]
      const findIndex = oldCorrectAnswersEdit.findIndex(
        (e) => e === oldAnswers[index]
      )
      oldCorrectAnswersEdit[findIndex] = editAnswer
      oldAnswers[index] = editAnswer
      setCorrectAnswers(oldCorrectAnswersEdit)
      setAnswers(oldAnswers)

      if (part.quizType === 'radio' && correctAnswers.length === 0) {
        const oldCorrectAnswers = [...correctAnswers]
        oldCorrectAnswers.push(editAnswer)
        setCorrectAnswers(oldAnswers)
      }
    }
  }

  const onDelete = (answer: string | null, index: number) => {
    if (answer) {
      const oldAnswers = [...answers]
      oldAnswers.splice(index, 1)
      setAnswers(oldAnswers)
    }
  }

  const onChangeCheckboxCorrectAnswer = (answer: string) => {
    const oldAnswers = [...correctAnswers]
    if (correctAnswers.includes(answer)) {
      const answerIndex = correctAnswers.indexOf(answer)
      oldAnswers.splice(answerIndex, 1)
      setCorrectAnswers(oldAnswers)
    } else {
      oldAnswers.push(answer)
      setCorrectAnswers(oldAnswers)
    }
  }

  const onChangeSelectCorrectAnswer = (answer: string) => {
    const oldAnswers = [...correctAnswers]
    oldAnswers.splice(0, 1)
    oldAnswers.push(answer)
    setCorrectAnswers(oldAnswers)
  }

  return (
    <PopUp>
      <EditCard>
        <StyledIcon onClick={onClose}>
          <Icon name={'cil:x'} color="darkRed" width={20} height={20} />
        </StyledIcon>
        <InputArea
          value={text}
          labelName="السؤال"
          onValueChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            setText(event.target.value)
          }
        />
        <div onClick={() => onAdd()}>
          <Icon
            name="fluent:add-square-24-regular"
            color="red"
            width={40}
            height={40}
          />
        </div>

        <StyledAnswers>
          <Cards>
            <Text>الإجابات الصحيحة</Text>
            {part.quizType === 'tickbox' ? (
              <QuizTick
                answers={answers}
                correctAnswers={correctAnswers}
                onValueChange={(correctAnswer) =>
                  onChangeCheckboxCorrectAnswer(correctAnswer)
                }
              />
            ) : part.quizType === 'dropdown' ? (
              <QuizSelect
                answers={answers}
                correctAnswers={correctAnswers}
                onValueChange={(correctAnswer) =>
                  onChangeSelectCorrectAnswer(correctAnswer)
                }
              />
            ) : (
              <QuizTrueFalse
                answers={answers}
                correctAnswers={correctAnswers}
                id={question.id}
                onValueChange={(correctAnswer) =>
                  onChangeSelectCorrectAnswer(correctAnswer)
                }
              />
            )}
          </Cards>

          <Cards>
            <Text>الإجابات</Text>
            {answers.map((answer, index) => (
              <Answer
                onDeleteClick={(answerToDelete) =>
                  onDelete(answerToDelete, index)
                }
                onEditClick={(answerToEdit) => onEdit(answerToEdit, index)}
                key={index}
                answer={answer}
              />
            ))}
          </Cards>
        </StyledAnswers>
        <Button
          loading={loading}
          onClick={question.id ? onSaveEdit : onSaveAdd}
          color="red"
        >
          <Icon name="entypo:check" />
        </Button>
      </EditCard>
    </PopUp>
  )
}

export default PartAddEditQuiz
